import { TreeItemData } from '@aibees/elephant/components/checkbox-tree-view';
import { Classification } from '@aibees/elephant/feature/search-filters';
import { useLoadFilterDataApi } from '@aibees/elephant/feature/search-filters/hooks/use-load-filter-api';
import {
  convertDataToKeyValueUseConfig,
  useOptionsUrl,
} from '@aibees/elephant/feature/search-filters/utils';
import { useSearchQueryMetadata } from '@aibees/elephant/hooks';
import { Icon, Scrollbar, Tab } from '@aibees/shared/components';
import { fHumanReadableNumber } from '@aibees/shared/utils';
import { TabContext, TabList } from '@mui/lab';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import { TreeViewBaseItem } from '@mui/x-tree-view';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useFilteredTreeItems } from '../../../autocomplete-tree-filter/hooks/use-filtered-tree-items';
import { transformAutocompleteOptionsToTreeItems } from '../../utils';
import { AutocompleteFilterOption } from '../autocomplete-filter-option';
import * as SX from './sx';
import type { AutocompleteFilterDropdownProps as Props } from './types';

export const AutocompleteFilterDropdown = memo(
  ({
    focused,
    metadata,
    config,
    utils,
    icon: filterIcon,
    options,
    searchKey,
    treeOptions,
    tabs,
    open: filterOpen,
    debouncedSearchQuery,
    selectedValues,
    onChangeSelection,
    ..._rest
  }: Props) => {
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<number>(0);
    const url = useOptionsUrl(options, tabValue, tabs);
    const hasTabs = !!tabs?.length && tabs?.length > 1;
    const boxHeight = theme.spacing(hasTabs ? 23 : 28);
    const apiParams = useMemo(
      () =>
        debouncedSearchQuery && !metadata?.autocomplete?.onlySearchInResults
          ? {
              [searchKey || (config?.labelKey as string)]: debouncedSearchQuery,
            }
          : {},
      [
        debouncedSearchQuery,
        metadata?.autocomplete?.onlySearchInResults,
        searchKey,
        config?.labelKey,
      ]
    );
    const optionsApi = useLoadFilterDataApi<Classification>(
      url,
      apiParams,
      utils,
      tabValue,
      options,
      metadata?.autocomplete?.tabsCountBadge ?? false,
      tabs
    );

    const loading = !!url && !!optionsApi && optionsApi.isLoading;

    const metadataApi = useSearchQueryMetadata(
      metadata?.metaDataApi?.key,
      !!(filterOpen && metadata?.metaDataApi?.enabled)
    );

    const metadataMap = useMemo(() => {
      const map = new Map<string, number>();
      metadataApi.data?.forEach((m) => {
        map.set(m.value.toLowerCase(), m.count ?? 0);
      });
      return map;
    }, [metadataApi.data]);

    // Static options: compute items for all tabs once at mount, then use by tab.
    const [itemsByTab, setItemsByTab] = useState<Map<
      number,
      TreeViewBaseItem<TreeItemData>[]
    > | null>(null);
    useEffect(() => {
      if (
        !url &&
        Array.isArray(treeOptions) &&
        treeOptions.length > 0 &&
        itemsByTab === null
      ) {
        const map = new Map<number, TreeViewBaseItem<TreeItemData>[]>();
        treeOptions.forEach((opt, i) => {
          if (Array.isArray(opt) && opt.length > 0) {
            map.set(i, opt as TreeViewBaseItem<TreeItemData>[]);
          }
        });
        if (map.size > 0) {
          setItemsByTab(map);
        }
      }
    }, [url, treeOptions, itemsByTab]);

    // Single pipeline for both API and static: get content, then normalize → convert → transform.
    // Only run optionsApiFormatter for API content; static treeOptions are already tree-shaped.
    const items = useMemo(() => {
      const content: unknown[] =
        optionsApi.data?.content ??
        itemsByTab?.get(tabValue) ??
        itemsByTab?.get(0) ??
        [];
      const rawData =
        url && utils?.optionsApiFormatter
          ? utils.optionsApiFormatter(
              content as Record<string, unknown>[],
              filterIcon,
              metadata?.delimiter
            )
          : (content as Record<string, unknown>[]);
      const cleanOptions = convertDataToKeyValueUseConfig(
        rawData,
        {
          labelKey: (config?.labelKey as string) ?? 'label',
          valueKey: (config?.valueKey as string) ?? 'value',
        },
        false,
        metadata?.autocomplete?.selectAllOption,
        utils?.valueFormatter,
        metadata?.delimiter
      );
      return transformAutocompleteOptionsToTreeItems(
        cleanOptions,
        utils?.tags?.labelFormatter,
        metadata?.delimiter,
        metadataMap
      );
      // Recompute when content source, tab, or metadata counts change. utils/config/metadata are stable.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, optionsApi.data?.content, itemsByTab, tabValue, metadataMap]);

    const filteredItems = useFilteredTreeItems({
      items,
      debouncedSearchQuery,
      isGettingDataFromApi: !!url,
      metadata,
      customOptionsLimit: metadata?.autocomplete?.customOptionsLimit,
    });

    const hasItemsToShow = filteredItems.length > 0;

    const multiSelect = metadata?.autocomplete?.multiple ?? false;
    const handleToggle = useCallback(
      (item: TreeViewBaseItem<TreeItemData>) => {
        if (multiSelect) {
          const next = selectedValues.includes(item.value)
            ? selectedValues.filter((v) => v !== item.value)
            : [...selectedValues, item.value];
          onChangeSelection(next, item.value);
        } else {
          onChangeSelection([item.value], item.value);
        }
      },
      [multiSelect, selectedValues, onChangeSelection]
    );

    const handleChangeTab = useCallback(
      (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
      },
      []
    );

    return (
      <Card variant="outlined" color={focused ? 'primary' : 'default'}>
        <CardContent padding="none">
          <Box
            display="flex"
            flexDirection="column"
            paddingX={0.5}
            paddingTop={hasTabs ? 1 : 0}
          >
            {hasTabs && (
              <TabContext value={tabValue}>
                <TabList
                  onChange={handleChangeTab}
                  variant="fullWidth"
                  size="medium"
                >
                  {tabs?.map((tab, i) => (
                    <Tab
                      key={tab}
                      label={tab}
                      value={i}
                      LabelVariant="soft"
                      LabelContent={
                        !url ? undefined : optionsApi.isLoading ? (
                          <Icon name="loading-right-outline" />
                        ) : (
                          fHumanReadableNumber(
                            optionsApi.data?.metadata?.tabsCount?.[i]
                          )
                        )
                      }
                    />
                  ))}
                </TabList>
              </TabContext>
            )}
            <Box height={!hasItemsToShow ? boxHeight : 'none'}>
              <Scrollbar sx={SX.scrollbar({ height: boxHeight })}>
                {loading ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                    height={boxHeight}
                    width="100%"
                  >
                    <Icon name="hourglass-empty" size={18} color="grey.400" />
                    <Typography variant="body5" color="grey.500">
                      Loading...
                    </Typography>
                  </Box>
                ) : hasItemsToShow ? (
                  <Box display="flex" flexDirection="column" paddingY={1}>
                    {filteredItems.map((item) => (
                      <AutocompleteFilterOption
                        key={item.id}
                        item={item}
                        checkboxPosition={
                          metadata?.autocomplete?.checkboxPosition ?? 'left'
                        }
                        checked={selectedValues.includes(item.value)}
                        onToggle={() => handleToggle(item)}
                        multiSelect={multiSelect}
                        metadataEnabled={metadata?.metaDataApi?.enabled}
                        metadataApiLoading={metadataApi.isFetching}
                        iconSx={metadata?.autocomplete?.iconSx}
                        avartarFallbackIcon={
                          metadata?.autocomplete?.avartarFallbackIcon
                        }
                      />
                    ))}
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                    height={boxHeight}
                    width="100%"
                  >
                    <Typography variant="body4" color="grey.500">
                      {debouncedSearchQuery
                        ? 'No results found'
                        : metadata?.emptyMessage || 'Nothing Here Yet'}
                    </Typography>
                  </Box>
                )}
              </Scrollbar>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  }
);

AutocompleteFilterDropdown.displayName = 'AutocompleteFilterDropdown';
